import { useDeferredValue, useState } from 'react';
import { Database, FileText, Loader2, Search, Video } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@lms/ui';
import type { StorageMediaObject, StorageProvider } from '@lms/shared/types';
import { useStorageMediaLibrary, useStorageProviders } from '../hooks/useMaterials';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface StoredMediaPickerProps {
  provider: StorageProvider;
  onProviderChange: (provider: StorageProvider) => void;
  onSelect: (media: StorageMediaObject) => void;
  disabled?: boolean;
}

function formatFileSize(size: number): string {
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function StoredMediaPicker({
  provider,
  onProviderChange,
  onSelect,
  disabled = false,
}: StoredMediaPickerProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const deferredSearch = useDeferredValue(search);
  const { data: providers } = useStorageProviders();
  const providerStatus = providers?.find(item => item.provider === provider);
  const { data, isLoading, isError } = useStorageMediaLibrary({
    provider,
    search: deferredSearch,
    cursor,
    enabled: open && Boolean(providerStatus?.available),
  });

  const selectMedia = (media: StorageMediaObject) => {
    onSelect(media);
    setOpen(false);
  };

  const changeProvider = (nextProvider: StorageProvider) => {
    setCursor(undefined);
    onProviderChange(nextProvider);
  };

  const changeSearch = (nextSearch: string) => {
    setCursor(undefined);
    setSearch(nextSearch);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="h-11 rounded-xl px-4 text-xs font-label font-bold active:scale-95"
      >
        <Database size={16} aria-hidden="true" />
        <Bilingual {...copy.materialModal.browseStoredMedia} />
      </Button>

      <DialogContent
        overlayClassName="z-[80]"
        className="z-[81] max-h-[85vh] max-w-2xl overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            <Bilingual {...copy.materialModal.storedMediaTitle} />
          </DialogTitle>
          <DialogDescription>
            <Bilingual {...copy.materialModal.storedMediaDescription} />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
            <div className="space-y-2">
              <label
                htmlFor="stored-media-provider"
                className="block text-xs font-label font-bold text-on-surface"
              >
                <Bilingual {...copy.materialModal.labelStorageProvider} />
              </label>
              <select
                id="stored-media-provider"
                value={provider}
                onChange={event => changeProvider(event.target.value as StorageProvider)}
                className="h-12 w-full rounded-xl bg-surface-container-low px-4 text-sm font-body font-medium text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              >
                <option value="CONTABO">{t(copy.materialModal.providerContabo)}</option>
                <option value="BIZNETGIO">{t(copy.materialModal.providerBiznetgio)}</option>
              </select>
              {providerStatus && !providerStatus.available && (
                <p className="text-xs font-medium text-error">
                  <Bilingual {...copy.materialModal.providerUnavailable} />
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label
                htmlFor="stored-media-search"
                className="block text-xs font-label font-bold text-on-surface"
              >
                <Bilingual {...copy.materialModal.storedMediaSearch} />
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
                <input
                  id="stored-media-search"
                  value={search}
                  onChange={event => changeSearch(event.target.value)}
                  placeholder={t(copy.materialModal.storedMediaSearch)}
                  className="h-12 w-full rounded-xl bg-surface-container-low px-4 pl-11 text-sm font-body text-on-surface outline-none placeholder:text-on-surface-variant/70 focus-visible:ring-2 focus-visible:ring-primary/20"
                />
              </div>
            </div>
          </div>

          {isLoading && (
            <div className="flex items-center gap-3 rounded-2xl bg-surface-container-low p-5 text-sm text-on-surface-variant">
              <Loader2 size={18} className="animate-spin text-primary" />
              <Bilingual {...copy.materialModal.storedMediaLoading} />
            </div>
          )}
          {isError && (
            <p
              role="alert"
              className="rounded-2xl bg-error-container p-4 text-sm text-on-error-container"
            >
              <Bilingual {...copy.materialModal.storedMediaLoadError} />
            </p>
          )}
          {!isLoading && !isError && providerStatus?.available && data?.items.length === 0 && (
            <p className="rounded-2xl bg-surface-container-low p-5 text-sm text-on-surface-variant">
              <Bilingual {...copy.materialModal.storedMediaEmpty} />
            </p>
          )}
          {data?.items.map(media => (
            <div
              key={media.key}
              className="flex items-center gap-3 rounded-2xl bg-surface-container-low p-4"
            >
              <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                {media.filename.match(/\.(mp4|webm|mov|mkv)$/i) ? (
                  <Video size={19} />
                ) : (
                  <FileText size={19} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-label font-bold text-on-surface">
                  {media.filename}
                </p>
                <p className="mt-0.5 text-xs text-on-surface-variant">
                  {formatFileSize(media.size)} · {media.provider}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => selectMedia(media)}
                className="h-10 shrink-0 rounded-xl px-3 text-xs font-label font-bold"
              >
                <Bilingual {...copy.materialModal.storedMediaSelect} />
              </Button>
            </div>
          ))}
          {data?.nextCursor && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setCursor(data.nextCursor ?? undefined)}
              className="h-11 w-full rounded-xl text-xs font-label font-bold"
            >
              <Bilingual {...copy.materialModal.storedMediaMore} />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
